/* Formats français : euros, entiers, dates. Le séparateur et l'espace
   insécable viennent d'Intl, pas d'une concaténation à la main. */

const eur = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const eur0 = new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const eur3 = new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", minimumFractionDigits: 3, maximumFractionDigits: 3,
});
const ent = new Intl.NumberFormat("fr-FR");

export const euro = (v: number) => eur.format(v || 0);
export const euroCourt = (v: number) => eur0.format(v || 0);
/** Un barème à l'unité se joue au millime : 0,060 € par sachet. */
export const euroPrecis = (v: number) => eur3.format(v || 0);
export const entier = (v: number) => ent.format(Math.round(v || 0));
export const pourcent = (v: number) => `${(v || 0).toFixed(2).replace(".", ",")} %`;

export function dateFr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

/** Ancienneté en clair : « aujourd'hui », « hier », « il y a 12 j ». */
export function jours(n: number): string {
  if (n < 0) return n === -1 ? "demain" : `dans ${-n} j`;
  if (n === 0) return "aujourd'hui";
  if (n === 1) return "hier";
  return `il y a ${n} j`;
}

/**
 * Nombre de jours écoulés depuis une date ISO, en jours de calendrier.
 *
 * On compte des minuits, pas des multiples de 24 h : sans cela, un document
 * daté d'aujourd'hui passait pour « hier » dès midi — l'écart dépassait la
 * demi-journée et l'arrondi basculait. Négatif pour une date à venir, ce dont
 * les échéances ont besoin.
 */
export function depuis(iso: string | null | undefined): number {
  if (!iso) return 0;
  const p = String(iso).slice(0, 10).split("-");
  if (p.length !== 3) return 0;
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  if (Number.isNaN(d.getTime())) return 0;
  const maintenant = new Date();
  const minuit = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
  return Math.round((minuit.getTime() - d.getTime()) / 86400000);
}

/** Taille d'un fichier, dans l'unité qui se lit d'un coup d'œil. */
export function poids(octets: number): string {
  const o = Math.max(0, octets || 0);
  if (o < 1024) return `${Math.round(o)} o`;
  if (o < 1024 * 1024) return `${Math.round(o / 1024)} ko`;
  return `${(o / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo`;
}

export function aujourdhui(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Un instant du fil : « aujourd'hui 14:32 », « hier 09:05 », « 12/09 à 16:40 ». */
export function momentFr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const heure = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  /* Le jour local, pas celui d'UTC : près de minuit les deux diffèrent, et
     c'est le jour qu'a vécu celui qui lit qui compte. */
  const jour0 = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const n = depuis(jour0);
  if (n === 0) return `aujourd'hui ${heure}`;
  if (n === 1) return `hier ${heure}`;
  const jour = String(d.getDate()).padStart(2, "0");
  const mois = String(d.getMonth() + 1).padStart(2, "0");
  if (d.getFullYear() === new Date().getFullYear()) return `${jour}/${mois} à ${heure}`;
  return `${jour}/${mois}/${d.getFullYear()}`;
}

/** Une échéance, dite du point de vue de l'attente : « dans 3 j », « en retard de 2 j ». */
export function echeanceFr(iso: string | null | undefined): string {
  if (!iso) return "sans échéance";
  const n = depuis(iso);
  if (n === 0) return "aujourd'hui";
  if (n === 1) return "hier";
  if (n > 1) return `en retard de ${n} j`;
  return n === -1 ? "demain" : `dans ${-n} j`;
}
